/**
 * Mention Picker Module
 * Handles keyboard navigation and event interception for mention picker dropdowns
 */

import { IMentionPickerModule } from '../types';

// Store active keyboard handlers for cleanup
const _keyHandlers = new Map<HTMLElement, (e: KeyboardEvent) => void>();

const MentionPickerModule: IMentionPickerModule = {
  /**
   * Scrolls the selected item into view within the mention picker dropdown
   * @param element - The selected item element to scroll into view
   */
  scrollIntoView(element: HTMLElement | null): void {
    if (!element) return;
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  },

  /**
   * Registers keyboard event handlers for mention picker interaction
   * Prevents default behavior for Enter, Tab, ArrowUp, ArrowDown, Escape
   * @param pickerElement - The mention picker dropdown container element
   * @param dotNetReference - .NET object reference for callbacks
   */
  registerKeyboardHandlers(pickerElement: HTMLElement, dotNetReference: DotNet.DotNetObject): void {
    if (!pickerElement) return;

    // Remove existing handler if any
    this.unregisterKeyboardHandlers(pickerElement);

    const handler = (e: KeyboardEvent) => {
      // Only handle events when focus is inside the comment textarea or picker
      const target = e.target as HTMLElement;
      const isInTextarea = target.tagName === 'TEXTAREA' || target.closest('textarea');
      const isInPicker = pickerElement.contains(target);

      if (!isInTextarea && !isInPicker) return;

      // Check if picker is actually visible in DOM (not hidden by parent)
      const isPickerVisible = pickerElement.offsetParent !== null;

      const key = e.key;

      switch (key) {
        case 'ArrowDown':
          // Only handle if picker is visible
          if (!isPickerVisible) return;
          e.preventDefault();
          e.stopPropagation();
          dotNetReference.invokeMethodAsync('OnArrowDown');
          break;

        case 'ArrowUp':
          // Only handle if picker is visible
          if (!isPickerVisible) return;
          e.preventDefault();
          e.stopPropagation();
          dotNetReference.invokeMethodAsync('OnArrowUp');
          break;

        case 'Enter':
          // Only handle if picker is visible, otherwise allow normal Enter behavior (new line)
          if (!isPickerVisible) return;
          e.preventDefault();
          e.stopPropagation();
          dotNetReference.invokeMethodAsync('OnEnter');
          break;

        case 'Tab':
          // Only prevent default when picker is visible
          if (isPickerVisible) {
            e.preventDefault();
            e.stopPropagation();
            dotNetReference.invokeMethodAsync('OnTab');
          }
          break;

        case 'Escape':
          // Only handle if picker is visible
          if (!isPickerVisible) return;
          e.preventDefault();
          e.stopPropagation();
          dotNetReference.invokeMethodAsync('OnEscape');
          break;

        case ' ':
          // Space after @ closes picker - handled in Blazor, but we can signal it
          // Only invoke if picker is visible
          if (isPickerVisible) {
            dotNetReference.invokeMethodAsync('OnSpace');
          }
          break;
      }
    };

    document.addEventListener('keydown', handler, { capture: true });
    _keyHandlers.set(pickerElement, handler);
  },

  /**
   * Unregisters keyboard event handlers for a mention picker
   * @param pickerElement - The mention picker dropdown container element
   */
  unregisterKeyboardHandlers(pickerElement: HTMLElement | null): void {
    if (!pickerElement) return;

    const handler = _keyHandlers.get(pickerElement);
    if (handler) {
      document.removeEventListener('keydown', handler, { capture: true } as AddEventListenerOptions);
      _keyHandlers.delete(pickerElement);
    }
  },

  /**
   * Scrolls to a specific item by its data-mention-index attribute
   * @param pickerElement - The mention picker dropdown container
   * @param index - The index of the item to scroll to
   */
  scrollToIndex(pickerElement: HTMLElement, index: number): void {
    if (!pickerElement) return;

    const item = pickerElement.querySelector(`[data-mention-index="${index}"]`) as HTMLElement;
    if (item) {
      this.scrollIntoView(item);
    }
  }
};

export default MentionPickerModule;
